// Mirrors routes/service_reports/control.py::_normalise_visits so the form refuses what the API
// would, with the same wording.

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const SIGNED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
// A report holds at most 31 visits; control.booked_visits refuses a longer period.
export const MAX_PERIOD_DAYS = 31;

function minutesOf(hhmm) {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
}

/** Aufenthalt (departure − arrival) and Arbeitszeit (that minus the break), in minutes. */
export function visitMinutes(visit) {
    const span = minutesOf(visit.end_time) - minutesOf(visit.start_time);
    return { onSite: span, work: span - (Number(visit.break_minutes) || 0) };
}

export function visitTotals(visits) {
    return (visits || []).reduce((acc, v) => {
        const { onSite, work } = visitMinutes(v);
        return {
            onSite: acc.onSite + (Number.isFinite(onSite) ? onSite : 0),
            work: acc.work + (Number.isFinite(work) ? work : 0),
            travel: acc.travel + (Number(v.travel_minutes) || 0),
        };
    }, { onSite: 0, work: 0, travel: 0 });
}

/** `7:45 h`, as on the PDF. */
export function fmtDuration(minutes) {
    if (!Number.isFinite(minutes) || minutes < 0) return '–';
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')} h`;
}

function dayLabel(iso) {
    const [y, m, d] = String(iso).split('-');
    return `${d}.${m}.${y}`;
}

/** Field by field, never through `new Date(iso)`: that is midnight UTC and lands a day early
 *  west of it. */
export function nextDay(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);
    const next = new Date(y, m - 1, d + 1);
    return [next.getFullYear(), next.getMonth() + 1, next.getDate()]
        .map((n, i) => String(n).padStart(i ? 2 : 4, '0'))
        .join('-');
}

function dayNumber(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);
    return Date.UTC(y, m - 1, d) / 86400000;
}

/** Null when the period can be looked up, otherwise why not, worded like the API. */
export function periodError(from, to) {
    if (!from || !to) return 'Bitte den Zeitraum angeben';
    if (to < from) return 'Das Ende liegt vor dem Anfang des Zeitraums';
    if (dayNumber(to) - dayNumber(from) + 1 > MAX_PERIOD_DAYS) {
        return `Der Zeitraum darf höchstens ${MAX_PERIOD_DAYS} Tage lang sein`;
    }
    return null;
}

/** First and last day of the visits, or `fallback` for both. */
export function periodOfVisits(visits, fallback) {
    const dates = (visits || []).map(v => v.date).filter(Boolean).sort();
    return { from: dates[0] || fallback, to: dates[dates.length - 1] || fallback };
}

export function bookedToVisit(booked) {
    return {
        date: booked.date,
        start_time: booked.start_time,
        end_time: booked.end_time,
        break_minutes: booked.break_minutes,
        travel_minutes: booked.travel_minutes,
    };
}

export function todayIso(now = new Date()) {
    return [now.getFullYear(), now.getMonth() + 1, now.getDate()]
        .map((n, i) => String(n).padStart(i ? 2 : 4, '0'))
        .join('-');
}

export function emptyVisit(date) {
    return { date, start_time: '08:00', end_time: '16:30', break_minutes: 30, travel_minutes: 0 };
}

/** The first problem with the visits, worded like the API's 422, or null. */
export function visitsError(visits) {
    if (!visits || visits.length === 0) return 'Mindestens ein Einsatztag ist nötig';
    for (const v of visits) {
        if (!v.date || !v.start_time || !v.end_time) {
            return 'Bitte Datum, Ankunft und Abfahrt für jeden Tag ausfüllen';
        }
    }
    const ordered = [...visits].sort((a, b) =>
        (a.date + a.start_time).localeCompare(b.date + b.start_time));
    for (const v of ordered) {
        const { onSite, work } = visitMinutes(v);
        if (!(onSite > 0)) return `Die Abfahrt muss nach der Ankunft liegen (${dayLabel(v.date)})`;
        if (!(work > 0)) return `Die Pause ist so lang wie der Aufenthalt (${dayLabel(v.date)})`;
    }
    for (let i = 1; i < ordered.length; i += 1) {
        const prev = ordered[i - 1];
        const cur = ordered[i];
        if (prev.date === cur.date && cur.start_time < prev.end_time) {
            return `Zwei Einsätze überschneiden sich (${dayLabel(cur.date)})`;
        }
    }
    return null;
}

export function reportToForm(report) {
    return {
        title: report.title || '',
        technician_id: report.technician_id ? String(report.technician_id) : '',
        location: report.location || '',
        contact_person: report.contact_person || '',
        work_description: report.work_description || '',
        remarks: report.remarks || '',
        language: report.language || 'de',
        visits: (report.visits || []).map(v => ({ ...v })),
    };
}

/** Every key is always sent: an emptied field has to reach the PATCH as null to be cleared. */
export function formToPayload(form) {
    const text = v => (v || '').trim() || null;
    return {
        title: form.title.trim(),
        technician_id: form.technician_id || null,
        location: text(form.location),
        contact_person: text(form.contact_person),
        work_description: text(form.work_description),
        remarks: text(form.remarks),
        language: form.language || 'de',
        visits: form.visits.map(v => ({
            date: v.date,
            start_time: v.start_time,
            end_time: v.end_time,
            break_minutes: Number(v.break_minutes) || 0,
            travel_minutes: Number(v.travel_minutes) || 0,
        })),
    };
}

/** Null when the file may be uploaded, otherwise why not. */
export function signedFileError(file) {
    if (!file) return 'Keine Datei ausgewählt';
    if (!SIGNED_TYPES.includes(file.type)) return 'Nur PDF, PNG oder JPEG';
    if (file.size > MAX_UPLOAD_BYTES) return 'Datei ist größer als 10 MB';
    return null;
}
